import React, { Fragment, useState } from 'react';
import { DashboardWidgetWrapper } from '@/pages/organization/components/DashboardWidgetWrapper';
import { UsageRow } from '@/pages/organization/components/UsageRow';
import { useParams } from 'react-router-dom';
import { useOrgTopShadowAiTools } from '@/hooks/api/useShadowAiTools';
import type { MCPServerUsage } from './types';
import { AIToolUserListModal } from '@/pages/organization/components/AIToolUserListModal';

const buildBrowserToolsUsageStats = (
  tools: Array<{
    toolId: string;
    name: string;
    userCount: number;
  }>
): MCPServerUsage[] => {
  if (!tools.length) return [];

  // Use the highest tool count as the total (for percentage calculations)
  const maxCount = Math.max(...tools.map(t => t.userCount));

  return tools.map(tool => ({
    id: tool.toolId,
    name: tool.name,
    usage: tool.userCount,
    total: maxCount,
  }));
};

type BrowserToolInfo = {
  toolId: string;
  name: string;
  userCount: number;
};

export const TopBrowserAITools: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [selectedTool, setSelectedTool] = useState<BrowserToolInfo | null>(
    null
  );

  // Use the top-active-tools endpoint with sourceType=browser filter
  const { data, isLoading, isError, refetch } = useOrgTopShadowAiTools(
    organizationId,
    {
      limit: 10,
      sourceType: 'browser',
    }
  );

  const usageData: MCPServerUsage[] = buildBrowserToolsUsageStats(
    data?.tools ?? []
  );

  const total = usageData[0]?.total ?? 0;

  const handleRowClick = (tool: MCPServerUsage) => {
    const toolInfo = data?.tools.find(t => t.toolId === tool.id);
    if (toolInfo) {
      setSelectedTool(toolInfo);
    }
  };

  const handleCloseModal = () => {
    setSelectedTool(null);
  };

  return (
    <Fragment>
      <DashboardWidgetWrapper
        className="xl:col-span-6 min-h-0"
        title="Top Browser AI Tools"
        description="Shows up to 10 Browser AI Tools"
        isLoading={isLoading}
        isError={isError}
        refetch={refetch}
      >
        {usageData.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            No data found
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-4 pr-2">
            {usageData.map(tool => (
              <UsageRow
                key={tool.id}
                id={tool.id}
                name={tool.name}
                usage={tool.usage}
                total={total}
                onClick={() => handleRowClick(tool)}
              />
            ))}
          </div>
        )}
      </DashboardWidgetWrapper>
      <AIToolUserListModal
        open={!!selectedTool}
        onClose={handleCloseModal}
        tool={selectedTool}
        emptyStateMessage="No users found for this browser AI tool"
      />
    </Fragment>
  );
};
